import { atom, selector } from 'recoil'
import { MAX_FILLED_CASE_IMAGES } from '@messenger/consts'
import type { CaseImageRecord } from '@/api/caseImages'
import type {
  CaseImageVariantListItem,
  CaseImageVariantPricing,
} from '@messenger/types'
import type { CaseImageVariantsById } from '@/utils/caseImages/groupCaseImageVariants'
import {
  resolveCaseImageDisplayUrl,
  type CaseImageTileDisplay,
} from '@/utils/caseImages/resolveCaseImageDisplayUrl'

export const caseImagesAtom = atom<CaseImageRecord[]>({
  key: 'caseImages',
  default: [],
})

/**
 * AI alteration variants of the open case, grouped by case image id, as
 * listed by the backend in one request. The grid reads these so a tile can
 * show an accepted enhancement and offer previous ones. Absent keys mean
 * "not loaded yet or none".
 */
export const caseImageVariantsAtom = atom<CaseImageVariantsById>({
  key: 'caseImageVariants',
  default: {},
})

/**
 * Price of accepting one variant of each kind, in cents, from the backend.
 * Null until loaded; the UI then shows the price line without a number
 * rather than a made-up amount.
 */
export const caseImageVariantPricingAtom = atom<CaseImageVariantPricing | null>(
  {
    key: 'caseImageVariantPricing',
    default: null,
  },
)

/** One entry per case image, in grid order, with the resolved tile image. */
export type CaseImageTile = {
  image: CaseImageRecord
  index: number
  display: CaseImageTileDisplay
  variants: CaseImageVariantListItem[]
}

/**
 * Each image joined with its variants and the URL its tile shows, in grid
 * order: what `caseImageTilesSelector` holds, built from the two atoms it
 * reads. Exported for a reader that has those atoms' values from somewhere
 * other than a committed snapshot.
 */
export const buildCaseImageTiles = (
  images: CaseImageRecord[],
  variantsById: CaseImageVariantsById,
): CaseImageTile[] =>
  images.map((image, index) => {
    const variants = variantsById[image.id] ?? []
    return {
      image,
      index,
      variants,
      display: resolveCaseImageDisplayUrl({ image, variants }),
    }
  })

/**
 * What the case images grid renders: each image joined with its variants and
 * the URL the tile should show. The editor and PDF use the backend's
 * `displayUrl` (see `displayImageUrlsSelector`), which applies the same rule
 * to the same rows (an accepted enhancement, unless it was reverted); the
 * grid reads the variants too, because its tile also offers the accepted
 * enhancement after a revert.
 */
export const caseImageTilesSelector = selector<CaseImageTile[]>({
  key: 'caseImageTiles',
  get: ({ get }) =>
    buildCaseImageTiles(get(caseImagesAtom), get(caseImageVariantsAtom)),
})

/**
 * The case photo that fills a template slot. Templates address photos by a
 * slot index (`properties.caseImage`) that can exceed the number of photos
 * the case has, so the photos repeat cyclically: slot 4 of a three-photo
 * case shows photo 1 again. This is the one place that rule lives; the
 * filled-images list and the per-item lookup both go through it. Returns
 * null when the case has no photos or the index is not a whole number.
 */
export const caseImageForSlot = (
  images: CaseImageRecord[],
  slotIndex: number,
): CaseImageRecord | null => {
  if (images.length === 0) return null
  if (!Number.isInteger(slotIndex) || slotIndex < 0) return null
  return images[slotIndex % images.length] ?? null
}

export const displayImageUrlsSelector = selector<string[]>({
  key: 'displayImageUrls',
  get: ({ get }) => {
    const images = get(caseImagesAtom)
    return images.map((img) => img.displayUrl)
  },
})

export const filledImagesSelector = selector<string[]>({
  key: 'filledImages',
  get: ({ get }) => {
    const images = get(caseImagesAtom)
    if (images.length === 0) return []
    // HOTFIX (#2569): cap was hardcoded to 20, which silently dropped slots
    // for templates referencing caseImage indices >= 20 (Guest Books,
    // Oversized Memorial Booklets, Photo Inserts). Prod max template index
    // is 156, so 300 gives ~2x headroom. Proper fix: modulo cycling in
    // sanitizeCaseImageUrl so this cap can go away entirely.
    return Array.from(
      { length: MAX_FILLED_CASE_IMAGES },
      (_, slotIndex) => caseImageForSlot(images, slotIndex)!.displayUrl,
    )
  },
})
